import '../datadog';
import '../hotjar-init';

import { BitkitProvider } from '@bitrise/bitkit-v2';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { AlertProvider } from '@/hooks/useAlert';
import Status500ErrorPageContent from '@/pages/error/ErrorPage/Status500ErrorPageContent';
import { dataDogErrorHandler } from '@/utils/dataDogErrorHandler';
import { globalProps } from '@/utils/globalProps';
import { queryClient } from '@/utils/query';

import { camelCaseKeys } from './changeCase';

type ReactComponent = Parameters<typeof createElement>[0];
type ReactProps = Parameters<typeof createElement>[1];

declare global {
  interface Window {
    pageProps: ReactProps;
  }
}

export function renderPageComponent(Page: ReactComponent, camelizeKeys = true): void {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('react-mount')!);
  root.render(
    <BitkitProvider>
      <ErrorBoundary FallbackComponent={Status500ErrorPageContent} onError={dataDogErrorHandler}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
          <AlertProvider>
            <GlobalPropsContext.Provider value={globalProps}>
              <Page {...(camelizeKeys ? camelCaseKeys(window.pageProps) : window.pageProps)} />
            </GlobalPropsContext.Provider>
          </AlertProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </BitkitProvider>,
  );
}
